import React from 'react';
import { Check, AlertTriangle } from 'lucide-react';
export { buildModalSegments, buildCardSegments } from './partsMapSegments';

/**
 * Shared segmented "parts map" — one segment per part, in chunk_index order.
 * The SAME visual object renders in the modal (preview of what processes now vs is
 * saved) and on the /pending card (live per-part state), so the user connects both.
 *
 * Segment kinds (color is never the ONLY cue — each has an icon and/or motion plus a
 * per-segment aria-label):
 *   done    emerald + ✓             (ready / partial)
 *   active  orange  + gentle pulse  (processing now)
 *   now     orange solid            (will process now — modal preview)
 *   queued  gray                    (pending / saved for later)
 *   failed  red + ▲ + inner border  (failed — needs retry)
 * `next: true` adds a 2px INNER orange outline via inset box-shadow — drawn inside the
 * segment bounds, so the container's overflow/rounding can never clip it.
 *
 * Aesthetics: segments share the width (flex-1, ≥3px) inside a rounded, clipped track,
 * with hairline gaps and a soft color transition on state change. Icons render only
 * when there's room (N ≤ 16); beyond that the bar reads as proportional progress and
 * the caption below names the exact next part.
 */
const KIND = {
  done:   { fill: 'bg-emerald-500', Icon: Check, aria: 'aria_done' },
  active: { fill: 'bg-[#FF6B00]/90 animate-pulse [animation-duration:2.4s]', Icon: null, aria: 'aria_active' },
  now:    { fill: 'bg-[#FF6B00]', Icon: null, aria: 'aria_now' },
  queued: { fill: 'bg-[#333333]', Icon: null, aria: 'aria_queued' },
  failed: { fill: 'bg-red-500', edge: 'border border-red-300/60', Icon: AlertTriangle, aria: 'aria_failed' },
  // ajustes-1-3-0 (Bloque A2, #499): el TERCER tramo de la barra de subida con series7: las
  // partes que no caben en ningun lado y hay que volver a subir. Hasta esta tanda se
  // pintaban como `queued` (guardadas), que era mentir. Gris propio, distinto del de
  // Pendientes, y su aria (T10 como estado).
  overflow: { fill: 'bg-[#3a3a3a]', Icon: null, aria: 'aria_overflow' },
};

/**
 * ajustes-1-3-0 (Bloque A2, #499): la lista de colores de la barra, debajo de ella y
 * con la tipografia de la leyenda. `tramos` son los que existen en ESTA barra
 * (`lib/series7.js::nombresDeLosTramos`: {kind, llave}), en el orden de la barra; cada
 * uno lleva una muestra del MISMO color que su tramo y su nombre (T10). Sin tramos no
 * pinta nada. Solo la pide la ventana de subida con series7: la barra de Pendientes y la
 * ventana sin series7 siguen byte por byte como hoy.
 */
// pantallas-alumno (G): con `estilo="alumno"` las muestras llevan los colores de la barra del alumno (la de abajo).
export function ColoresDeLaBarra({ tramos = [], t, testid = 'parts-map-colores', estilo }) {
  if (!tramos.length) return null;
  const alumno = estilo === 'alumno';
  return (
    <ul className={alumno ? 'flex flex-wrap gap-x-3 gap-y-1 text-[13px] text-[#C8C8C8] mt-2' : 'flex flex-wrap gap-x-3 gap-y-1 text-[13px] text-[#cccccc] mt-1.5'} data-testid={testid}>
      {tramos.map((tr) => (
        <li key={tr.kind} className="flex items-center gap-1.5">
          <span aria-hidden="true"
            className={`inline-block w-2.5 h-2.5 rounded-sm shrink-0 ${alumno ? ((KIND_ALUMNO[tr.kind] || KIND_ALUMNO.queued).fill) : (KIND[tr.kind] || KIND.queued).fill}`}
            style={alumno && (KIND_ALUMNO[tr.kind] || {}).rayas ? RAYAS_DE_PROCESANDO : undefined} />
          {t(tr.llave)}
        </li>
      ))}
    </ul>
  );
}

/* pantallas-alumno (G), parte 2 "Mapa de partes" (pieza propia de Pendientes): barra de 22 px con un segmento
   por parte (3 px de separacion, radio 4). Lista #4ADE80 con palomita oscura; procesando con las rayas claras de la
   barra de procesando del Panel; fallida #F87171 con triangulo oscuro; la siguiente #1B1B1B con contorno naranja de
   2 px; pendiente #262626; "se procesa ahora" naranja lleno. Los iconos, con el mismo corte de hoy (16 partes). La
   etiqueta accesible y la de cada segmento son las de hoy. Solo con `estilo="alumno"`: sin el, la barra de siempre
   (la de la ventana del documento grande del Panel), byte por byte. */
const KIND_ALUMNO = {
  done: { fill: 'bg-[#4ADE80]', icono: 'text-[#0F1F16]' },
  active: { fill: '', rayas: true },
  now: { fill: 'bg-[#F27A2E]' },
  queued: { fill: 'bg-[#262626]' },
  failed: { fill: 'bg-[#F87171]', icono: 'text-[#2A1414]' },
  overflow: { fill: 'bg-[#3a3a3a]' },
};
const RAYAS_DE_PROCESANDO = { backgroundImage: 'repeating-linear-gradient(135deg, #D4D4D4 0 6px, #BDBDBD 6px 12px)' };

function PartsMapDelAlumno({ segments, t, testid }) {
  const showIcons = segments.length <= 16;
  return (
    <div className="flex gap-[3px] w-full" role="img" data-testid={testid} data-estilo="alumno"
      aria-label={t('dashboard:parts_map.bar_label', { parts: t('dashboard:auto_split.parts_amount', { count: segments.length }) })}>
      {segments.map((s, idx) => {
        const spec = KIND[s.kind] || KIND.queued;
        const al = KIND_ALUMNO[s.kind] || KIND_ALUMNO.queued;
        const Icon = spec.Icon;
        const i = s.index ?? idx + 1;
        const siguiente = Boolean(s.next) && s.kind === 'queued';
        const fondo = siguiente ? 'bg-[#1B1B1B]' : al.fill;
        const borde = s.next ? 'border-2 border-[#F27A2E]' : '';
        return (
          <div key={idx}
            className={`relative flex-1 min-w-[3px] h-[22px] rounded-[4px] flex items-center justify-center ${fondo} ${borde}`}
            style={al.rayas ? RAYAS_DE_PROCESANDO : undefined}
            aria-label={t(`dashboard:parts_map.${spec.aria}`, { i })}
            data-testid={`${testid}-seg-${i}`} data-kind={s.kind} data-next={s.next ? '1' : '0'}>
            {showIcons && Icon && <Icon className={`w-3 h-3 ${al.icono || 'text-white'}`} strokeWidth={3} />}
          </div>
        );
      })}
    </div>
  );
}

export default function PartsMap({ segments = [], t, testid = 'parts-map', estilo }) {
  if (!segments.length) return null;
  if (estilo === 'alumno') return <PartsMapDelAlumno segments={segments} t={t} testid={testid} />;
  const showIcons = segments.length <= 16;
  return (
    <div
      className="flex gap-px w-full"
      role="img"
      data-testid={testid}
      aria-label={t('dashboard:parts_map.bar_label', { parts: t('dashboard:auto_split.parts_amount', { count: segments.length }) })}
    >
      {segments.map((s, idx) => {
        const spec = KIND[s.kind] || KIND.queued;
        const Icon = spec.Icon;
        const i = s.index ?? idx + 1;
        // Edge segments carry their own rounding (no track clipping), so borders —
        // which follow border-radius — trace the VISIBLE shape on every segment,
        // including N=1/N=2 where every segment is an edge.
        const shape = `${idx === 0 ? 'rounded-l-md' : ''} ${idx === segments.length - 1 ? 'rounded-r-md' : ''}`;
        const edge = s.next ? 'border-2 border-[#FF6B00]' : (spec.edge || '');
        return (
          <div
            key={idx}
            className={`relative flex-1 min-w-[3px] h-5 md:h-6 flex items-center justify-center transition-colors duration-500 ${spec.fill} ${shape} ${edge}`}
            aria-label={t(`dashboard:parts_map.${spec.aria}`, { i })}
            data-testid={`${testid}-seg-${i}`}
            data-kind={s.kind}
            data-next={s.next ? '1' : '0'}
          >
            {showIcons && Icon && <Icon className="w-3 h-3 text-white drop-shadow-sm" />}
          </div>
        );
      })}
    </div>
  );
}
